import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  IconButton,
  InputAdornment,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,

} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { useEffect, useMemo, useState } from "react";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import { MUTED, SURFACE, SURFACE_HEAD, BORDER, Panel } from "../../../dashboard/dashboard-components/resellerUi";
import { mapTipoToDescrizione } from "../lecsType";
import API from "../../../../api";
import renderNotification from "../../../../utils/notification-handler";
import { startToast, updateToastSuccess, updateToastError } from '../../../../utils/notification-loading';
import { dataGridStyle, palette } from "../../../../theme";

const ManageLocalAdmins = () => {
  const [admins, setAdmins] = useState([]);
  const [deviceDialogOpen, setDeviceDialogOpen] = useState(false);
  const [devices, setDevices] = useState([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [search, setSearch] = useState("");

  const [newAdmin, setNewAdmin] = useState({
    name: "",
    surname: "",
    email: "",
    password: "",
    confirmPassword: "",
    commercial: false
  });


  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [logoError, setLogoError] = useState("");
  const [imgBase64, setImgBase64] = useState("");

  const MAX_WIDTH = 600;
  const MAX_HEIGHT = 400;

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    setLogoError("");
    setLogoFile(null);
    setLogoPreview("");

    if (!file) return;

    if (file.type !== "image/png") {
      setLogoError("Il file deve essere un PNG.");
      return;
    }

    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const { width, height } = img;
      URL.revokeObjectURL(url);

      if (width <= MAX_WIDTH && height <= MAX_HEIGHT) {
        setLogoFile(file);
        setLogoPreview(URL.createObjectURL(file));

        // 🔥 Converti in base64 e invia
        const reader = new FileReader();
        reader.onloadend = () => {
          API.AddImageReseller({ img: reader.result });
        };
        reader.readAsDataURL(file);
      } else {
        setLogoError(
          `Dimensions too large: ${width}×${height}px. 
          Maximum allowed: ${MAX_WIDTH}×${MAX_HEIGHT}px.`
        );
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      setLogoError("Unable to read image. Try again with a valid PNG.");
    };
    img.src = url;
  };




  const clearLogo = () => {
    setLogoFile(null);
    setLogoPreview("");
    setLogoError("");
  };

  useEffect(() => {
    API.GetImageReseller().then((result) => {
      setImgBase64(result.data.img);
    });
    
  }, []);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const response = await API.getLocalAdmins({type: "table"});
      const formattedAdmins = response.data.map((admin) => ({
        ...admin,
        id: admin._id,
      }));
      setAdmins(formattedAdmins);
    } catch (error) {
      renderNotification("error", "Error", "Error fetching admins");
    }
  };

  const isPasswordValid = (password) => {
    const regex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;
    return regex.test(password);
  };

  const addAdmin = async () => {
    startToast();
    const { name, surname, email, password, confirmPassword, commercial } = newAdmin;

    if (!isPasswordValid(password)) {
      updateToastError("Password must be at least 8 characters, include one uppercase letter, one number, and one special character.");
      return;
    }

    if (password !== confirmPassword) {
      updateToastError("Passwords do not match.");
      return;
    }
    
    try {
      const payload = { name, surname, email, psw: password , commercial: commercial};
      await API.addAdminUser(payload);
      fetchAdmins();
      setAddDialogOpen(false);
      setNewAdmin({
        name: "",
        surname: "",
        email: "",
        password: "",
        confirmPassword: "",
        commercial: false,
      });
      updateToastSuccess("Admin added successfully");
    } catch (error) {
      updateToastError("Error on admin creation");
    }
  };

  const deleteAdmin = async (adminId) => {
    try {
      startToast();
      await API.delAdminUser({ email: adminId });
      fetchAdmins();
      updateToastSuccess("Admin deleted successfully");
    } catch (error) {
      updateToastError("Error deleting admin");
    }
  };

  const fetchDevicesForAdmin = async (adminId) => {
    try {
      const result = await API.getLocalAdminLecs({ adminCode: adminId });
      setDevices(result.data || []);
      setDeviceDialogOpen(true);
    } catch (error) {
      renderNotification("error", "Error", "Error fetching devices");
    }
  };

  const filteredAdmins = useMemo(() => {
    const q = search.trim().toLowerCase();
    return admins.filter(
      (a) =>
        !q ||
        [a.nome, a.cognome, a.email, a.code].some((v) => String(v ?? "").toLowerCase().includes(q))
    );
  }, [admins, search]);

  const outlineBtnSx = {
    color: "#eaeaea",
    borderColor: "rgba(255,255,255,0.14)",
    "&:hover": { borderColor: palette.primary.light, backgroundColor: "rgba(69,144,149,0.12)" },
  };

  const paperSx = { backgroundColor: SURFACE, backgroundImage: "none", border: `1px solid ${BORDER}`, borderRadius: "10px" };
  const titleSx = { display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.25, backgroundColor: SURFACE_HEAD, fontSize: 16, fontWeight: 700 };

  const adminColumns = [
    { field: "nome", headerName: "Name", flex: 1, minWidth: 120 },
    { field: "cognome", headerName: "Surname", flex: 1, minWidth: 120 },
    { field: "email", headerName: "Email", flex: 1.6, minWidth: 220 },
    { field: "code", headerName: "Admin code", width: 120 },
    {
      field: "auth",
      headerName: "Type",
      width: 130,
      renderCell: (params) => {
        const commercial = params.row.auth === "commercial";
        const c = commercial ? "#EF9F27" : palette.primary.light;
        return (
          <Box sx={{ display: "inline-flex", px: 0.8, py: 0.15, borderRadius: 0.5, color: c, bgcolor: `${c}22`, border: `1px solid ${c}`, fontSize: 11, fontWeight: 700, lineHeight: 1.5 }}>
            {commercial ? "COMMERCIAL" : "LOCAL ADMIN"}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      width: 190,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box display="flex" gap={1} alignItems="center">
          {params.row.auth !== "commercial" && (
            <Button size="small" variant="outlined" sx={outlineBtnSx} onClick={() => fetchDevicesForAdmin(params.row.code)}>
              Devices
            </Button>
          )}
          <Tooltip title="Delete account" arrow>
            <IconButton size="small" aria-label="Delete account" onClick={() => deleteAdmin(params.row.email)}>
              <DeleteOutlineIcon fontSize="small" color="error" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const toolbar = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
      <TextField
        size="small"
        placeholder="Search by name, email or code…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ minWidth: 260, flex: 1, maxWidth: 420 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: MUTED }} />
              </InputAdornment>
            ),
          },
        }}
      />
      <Box sx={{ flex: 1 }} />
      <Button variant="contained" startIcon={<PersonAddAltOutlinedIcon />} onClick={() => setAddDialogOpen(true)}>
        Create account
      </Button>
    </Box>
  );

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Box mb={2}>{toolbar}</Box>
          <Box sx={{ height: "calc(100vh - 290px)", minHeight: 380 }}>
            <DataGridPro
              rows={filteredAdmins}
              columns={adminColumns}
              disableRowSelectionOnClick
              localeText={{ noRowsLabel: "No accounts found" }}
              sx={dataGridStyle}
              initialState={{
                sorting: { sortModel: [{ field: "auth", sort: "asc" }] },
              }}
            />
          </Box>
        </Grid>

        {/* Uploader PNG */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Panel title="Logo" sx={{ height: "auto", minHeight: 0 }}>
            <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography sx={{ fontSize: 13, color: MUTED }}>
                PNG with maximum dimensions <strong>600x400px</strong>.
              </Typography>

              <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
                <Button variant="outlined" component="label" sx={outlineBtnSx}>
                  Select PNG
                  <input hidden type="file" accept="image/png" onChange={handleLogoChange} />
                </Button>

                {logoFile && <Button onClick={clearLogo}>Remove</Button>}
              </Box>

              {logoError && (
                <Typography variant="body2" color="error">
                  {logoError}
                </Typography>
              )}

              {(logoPreview || imgBase64) ? (
                <Box
                  component="img"
                  src={logoPreview || imgBase64}
                  alt="Logo preview"
                  sx={{ width: "100%", maxHeight: 200, objectFit: "contain", borderRadius: "8px", border: `1px solid ${BORDER}`, background: "#111" }}
                />
              ) : (
                <Typography sx={{ color: MUTED }}>No logo uploaded.</Typography>
              )}
            </Box>
          </Panel>
        </Grid>
      </Grid>

      {/* Dialog per aggiungere admin */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} fullWidth maxWidth="sm" slotProps={{ paper: { sx: paperSx } }}>
      <DialogTitle sx={titleSx}>Create New Admin Account</DialogTitle>
      <DialogContent sx={{ pt: "20px !important" }}>
        <TextField
          fullWidth
          margin="dense"
          label="Name"
          variant="outlined"
          value={newAdmin.name}
          onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Surname"
          variant="outlined"
          value={newAdmin.surname}
          onChange={(e) => setNewAdmin({ ...newAdmin, surname: e.target.value })}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Email"
          variant="outlined"
          type="email"
          value={newAdmin.email}
          onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Password"
          variant="outlined"
          type="password"
          value={newAdmin.password}
          onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Confirm Password"
          variant="outlined"
          type="password"
          value={newAdmin.confirmPassword}
          onChange={(e) => setNewAdmin({ ...newAdmin, confirmPassword: e.target.value })}
        />
        <FormControl fullWidth margin="dense" variant="outlined" sx={{ mt: 2 }}>
          <InputLabel id="permissions-label">Permissions</InputLabel>
          <Select
            labelId="permissions-label"
            id="permissions"
            value={newAdmin.commercial}
            onChange={(e) => setNewAdmin({ ...newAdmin, commercial: e.target.value })}
            label="Permissions"
          >
            <MenuItem value={false}>All</MenuItem>
            <MenuItem value={true}>Commercial</MenuItem>
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
        <Button variant="contained" color="primary" onClick={addAdmin}>
          Create
        </Button>
      </DialogActions>
    </Dialog>

      {/* Dialog per visualizzare i dispositivi */}
      <Dialog open={deviceDialogOpen} onClose={() => setDeviceDialogOpen(false)} maxWidth="sm" fullWidth slotProps={{ paper: { sx: paperSx } }}>
        <DialogTitle sx={titleSx}>
          Devices
          <IconButton size="small" onClick={() => setDeviceDialogOpen(false)} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: "20px !important" }}>
          {devices.length === 0 ? (
            <Typography sx={{ color: MUTED }}>No devices assigned.</Typography>
          ) : (
            <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: "10px", overflow: "hidden" }}>
              {devices.map((device, index) => (
                <Box
                  key={device.matricola ?? index}
                  sx={{ px: 2, py: 1.25, borderTop: index === 0 ? "none" : `1px solid ${BORDER}` }}
                >
                  <Typography noWrap>
                    <Box component="span" sx={{ fontWeight: 700, color: palette.primary.light }}>{device.matricola}</Box>
                    <Box component="span" sx={{ ml: 1, fontWeight: 600 }}>{device.nomeLecs || device.name || "Unnamed Device"}</Box>
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: MUTED }}>{mapTipoToDescrizione(device.tipo)}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ManageLocalAdmins;
